import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, configureApi, ApiError } from '../lib/api';

export interface AuthState {
  apiUrl: string;
  token: string;
  tenant: string;
  user: string;
  scopes: string[];
  clientId: string | null;
}

const EMPTY: AuthState = { apiUrl: '', token: '', tenant: '', user: '', scopes: [], clientId: null };
const STORAGE_KEY = 'cp_session';

function loadSaved(): AuthState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...EMPTY, ...parsed };
  } catch { return null; }
}
function persist(s: AuthState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* stockage indisponible, tant pis */ }
}
function clearPersist() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* rien à nettoyer */ }
}

interface AuthCtx {
  state: AuthState;
  ready: boolean; // true une fois la session restaurée (ou confirmée absente)
  login: (apiUrl: string, tenant: string, user: string, pin: string) => Promise<void>;
  logout: () => void;
  hasScope: (...scopes: string[]) => boolean;
  canAny: (required: string[]) => boolean;
  isClientOwnerOnly: boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(EMPTY);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const saved = loadSaved();
    if (saved && saved.token) {
      configureApi(saved.apiUrl, saved.token);
      if (saved.scopes && saved.scopes.length) {
        setState(saved);
        setReady(true);
      } else {
        // ancienne session sans scopes en cache : on les redemande
        api<{ scopes: string[]; client_id: string | null }>('/v1/me')
          .then((me) => {
            const next = { ...saved, scopes: me.scopes || [], clientId: me.client_id || null };
            persist(next);
            setState(next);
          })
          .catch(() => setState(saved))
          .finally(() => setReady(true));
      }
    } else {
      setReady(true);
    }
  }, []);

  async function login(apiUrl: string, tenant: string, user: string, pin: string) {
    const cleanUrl = apiUrl.trim().replace(/\/+$/, '');
    configureApi(cleanUrl, '');
    const data = await api<{ token: string }>('/v1/auth/login', {
      method: 'POST',
      body: { tenant, user, pin },
    });
    configureApi(cleanUrl, data.token);
    const me = await api<{ scopes: string[]; client_id: string | null; user: string }>('/v1/me');
    const next: AuthState = {
      apiUrl: cleanUrl,
      token: data.token,
      tenant,
      user: me.user || user,
      scopes: me.scopes || [],
      clientId: me.client_id || null,
    };
    persist(next);
    setState(next);
  }

  function logout() {
    clearPersist();
    configureApi(state.apiUrl, '');
    setState({ ...EMPTY, apiUrl: state.apiUrl });
  }

  function hasScope(...scopes: string[]) {
    return scopes.some((s) => state.scopes.indexOf(s) !== -1);
  }
  function canAny(required: string[]) {
    if (state.scopes.indexOf('admin') !== -1) return true;
    return required.some((r) => state.scopes.indexOf(r) !== -1);
  }
  const isClientOwnerOnly = state.scopes.length > 0 && state.scopes.every((s) => s === 'self');

  return (
    <Ctx.Provider value={{ state, ready, login, logout, hasScope, canAny, isClientOwnerOnly }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth doit être utilisé dans <AuthProvider>');
  return ctx;
}

export { ApiError };
